/**
 * Firestore CRUD for PDF take-off links (measurement → timber item).
 */

import { db } from './firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  doc,
  Timestamp,
} from 'firebase/firestore';
import type { PdfTakeoffLink } from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';

const COLLECTION = 'pdfTakeoffLinks';

function linkFromDoc(snap: { id: string; data: () => Record<string, unknown> }): PdfTakeoffLink {
  const d = snap.data();
  const created = d.createdAt as { toDate?: () => Date } | undefined;
  const updated = d.updatedAt as { toDate?: () => Date } | undefined;
  return {
    id: snap.id,
    documentId: d.documentId as string,
    documentUrl: d.documentUrl as string,
    dealId: d.dealId as string,
    leadId: d.leadId as string,
    measurementId: d.measurementId as string,
    pageNumber: d.pageNumber as number,
    structureId: d.structureId as string,
    structureName: d.structureName as string,
    timberItemId: (d.timberItemId as string) || undefined,
    name: (d.name as string) || '',
    species: (d.species as string) || '',
    grade: (d.grade as string) || '',
    dimensions: (d.dimensions as string) || '',
    quantity: typeof d.quantity === 'number' ? d.quantity : 1,
    moistureContent: (d.moistureContent as PdfTakeoffLink['moistureContent']) || '',
    measuredLength: typeof d.measuredLength === 'number' ? d.measuredLength : undefined,
    measuredLengthUnit: (d.measuredLengthUnit as 'ft' | 'in') || undefined,
    unitCost: typeof d.unitCost === 'number' ? d.unitCost : undefined,
    notes: (d.notes as string) || undefined,
    userId: d.userId as string,
    userName: (d.userName as string) || 'Unknown',
    createdAt: created?.toDate?.() ?? new Date(),
    updatedAt: updated?.toDate?.() ?? undefined,
  };
}

export function subscribeDocumentTakeoffLinks(
  documentId: string,
  callback: (list: PdfTakeoffLink[]) => void,
): () => void {
  const q = query(collection(db, COLLECTION), where('documentId', '==', documentId));
  return onSnapshot(q, snap => {
    const list = snap.docs.map(d => linkFromDoc({ id: d.id, data: () => d.data() }));
    list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

/** Single listener for all take-off links on a lead (avoids one listener per document row). */
export function subscribeToTakeoffLinksForLead(
  leadId: string,
  callback: (list: PdfTakeoffLink[]) => void,
): () => void {
  const q = query(collection(db, COLLECTION), where('leadId', '==', leadId));
  return onSnapshot(q, snap => {
    const list = snap.docs.map(d => linkFromDoc({ id: d.id, data: () => d.data() }));
    list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

/** One-shot fetch of all take-off links for a deal (all plan files). */
export async function fetchDealTakeoffLinks(dealId: string): Promise<PdfTakeoffLink[]> {
  const q = query(collection(db, COLLECTION), where('dealId', '==', dealId));
  const snap = await getDocs(q);
  const list = snap.docs.map(d => linkFromDoc({ id: d.id, data: () => d.data() }));
  list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  return list;
}

export async function createTakeoffLink(
  input: Omit<PdfTakeoffLink, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const payload = removeUndefinedDeep({
    ...input,
    createdAt: Timestamp.now(),
  });
  const ref = await addDoc(collection(db, COLLECTION), payload);
  return ref.id;
}

export async function updateTakeoffLink(id: string, patch: Partial<PdfTakeoffLink>): Promise<void> {
  const payload = removeUndefinedDeep({
    ...patch,
    updatedAt: Timestamp.now(),
  });
  await updateDoc(doc(db, COLLECTION, id), payload);
}

export async function deleteTakeoffLink(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

export async function deleteTakeoffLinkByMeasurementId(measurementId: string): Promise<void> {
  const q = query(collection(db, COLLECTION), where('measurementId', '==', measurementId));
  const snap = await getDocs(q);
  await Promise.all(snap.docs.map(d => deleteDoc(doc(db, COLLECTION, d.id))));
}
